import { useEffect, useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { createInstancedAssetGroup } from '../../../utils/instancedAsset'
import {
    obstacleScaleStartDistance,
    obstacleScaleEndDistance,
    obstacleModels,
    lanePositions,
} from '../../config/constants'
import type { TrackController } from '../track/trackController'
import { useRunState } from '../../provider/useRunState'
import { buildObstacleCollisionData, checkObstacleCollision } from './obstacleCollision'
import { useTrackController } from '../track/useTrackController'
import {
    obstaclePositions,
    type PathObstaclePlacement,
} from '../../config/obstaclePositions'
import { ObstacleHitboxDebug } from './ObstacleHitboxDebug'
import { store } from '@/src/stores/store'
import {
    ObstacleDestroyEffect,
    type ObstacleDestroyBurstConfig,
} from './ObstacleDestroyEffect'
import {
    BatteryObstacleLightningEffect,
    type BatteryObstacleLightningStrikeConfig,
} from './BatteryObstacleLightningEffect'
import {
    BATTERY_OBSTACLE_LIGHTNING_EVENT,
    type BatteryObstacleLightningEventDetail,
} from './batteryObstacleLightningEvents'
import { applyMaterial, useAtlasMaterial } from '../../materials/atlasMaterial'
import { disposeObject } from '../../../utils/threeDisposal'
import { vibrate } from '@/src/lib/vibration'

type ObstacleAssetsByRegion = {
    1: Partial<Record<number, THREE.Object3D>>
    2: Partial<Record<number, THREE.Object3D>>
    3: Partial<Record<number, THREE.Object3D>>
    4: Partial<Record<number, THREE.Object3D>>
}

type ResolvedObstaclePlacement = PathObstaclePlacement & {
    sourceIndex: number
    region: 1 | 2 | 3 | 4
}

type ObstacleInstance = {
    sourceIndex: number
    position: number
    type: number
    region: ResolvedObstaclePlacement['region']
    asset: THREE.Object3D | null
    worldPosition: THREE.Vector3
    worldRotation: THREE.Quaternion
}

type ObstaclesController = {
    group: THREE.Group
    obstacleInstancesById: Map<string, ObstacleInstance>
    obstacleLocalBoundsById: Map<string, THREE.Box3>
    update: (trackPosition: number, hiddenIds?: Set<string>) => void
}

export function ObstacleController() {
    const trackController = useTrackController()
    const { scene: level1_type1_Scene } = useGLTF(obstacleModels.level1.type1)
    const { scene: level1_type2_Scene } = useGLTF(obstacleModels.level1.type2)
    const { scene: level1_type3_Scene } = useGLTF(obstacleModels.level1.type3)
    const { scene: level1_type6_Scene } = useGLTF(obstacleModels.level1.type6)
    const { scene: level2_type1_Scene } = useGLTF(obstacleModels.level2.type1)
    const { scene: level2_type2_Scene } = useGLTF(obstacleModels.level2.type2)
    const { scene: level2_type4_Scene } = useGLTF(obstacleModels.level2.type4)
    const { scene: level2_type5_Scene } = useGLTF(obstacleModels.level2.type5)
    const { scene: level3_type1_Scene } = useGLTF(obstacleModels.level3.type1)
    const { scene: level3_type4_Scene } = useGLTF(obstacleModels.level3.type4)
    const { scene: level3_type5_Scene } = useGLTF(obstacleModels.level3.type5)
    const { scene: level3_type6_Scene } = useGLTF(obstacleModels.level3.type6)
    const { scene: level4_type3_Scene } = useGLTF(obstacleModels.level4.type3)
    const { scene: level4_type5_Scene } = useGLTF(obstacleModels.level4.type5)
    const { scene: level4_type6_Scene } = useGLTF(obstacleModels.level4.type6)
    const { scene: level4_type7_Scene } = useGLTF(obstacleModels.level4.type7)

    const runState = useRunState()
    const atlasMaterial = useAtlasMaterial()
    const placements = useMemo(
        () =>
            obstaclePositions.map((placement, sourceIndex) => ({
                ...placement,
                sourceIndex,
                region: trackController.getTrackRegionState(placement.position)
                    .activeRegion,
            })),
        [trackController],
    )
    const assetsByRegion = useMemo<ObstacleAssetsByRegion>(
        () => ({
            1: {
                1: level1_type1_Scene,
                2: level1_type2_Scene,
                3: level1_type3_Scene,
                6: applyMaterial(level1_type6_Scene, atlasMaterial),
            },
            2: {
                1: applyMaterial(level2_type1_Scene, atlasMaterial),
                2: applyMaterial(level2_type2_Scene, atlasMaterial),
                4: applyMaterial(level2_type4_Scene, atlasMaterial),
                5: applyMaterial(level2_type5_Scene, atlasMaterial),
            },
            3: {
                1: applyMaterial(level3_type1_Scene, atlasMaterial),
                4: applyMaterial(level3_type4_Scene, atlasMaterial),
                5: applyMaterial(level3_type5_Scene, atlasMaterial),
                6: applyMaterial(level3_type6_Scene, atlasMaterial),
            },
            4: {
                3: level4_type3_Scene,
                5: level4_type5_Scene,
                6: level4_type6_Scene,
                7: level4_type7_Scene,
            },
        }),
        [
            level1_type1_Scene,
            level1_type2_Scene,
            level1_type3_Scene,
            level1_type6_Scene,
            level2_type1_Scene,
            level2_type2_Scene,
            level2_type4_Scene,
            level2_type5_Scene,
            level3_type1_Scene,
            level3_type4_Scene,
            level3_type5_Scene,
            level3_type6_Scene,
            level4_type3_Scene,
            level4_type5_Scene,
            level4_type6_Scene,
            level4_type7_Scene,
            atlasMaterial,
        ],
    )

    const collisionData = useMemo(
        () => buildObstacleCollisionData(placements, trackController),
        [placements, trackController],
    )

    const obstaclesController = useMemo(
        () => createObstacles(
            assetsByRegion,
            placements,
            trackController,
        ),
        [assetsByRegion, placements, trackController],
    )
    useEffect(() => {
        return () => {
            disposeObject(obstaclesController.group)
        }
    }, [obstaclesController])
    const processedHiddenIdsRef = useRef<Set<string>>(new Set())
    const emittedBurstIdsRef = useRef<Set<string>>(new Set())
    const destroyBurstQueueRef = useRef<ObstacleDestroyBurstConfig[]>([])
    const batteryStrikeQueueRef = useRef<BatteryObstacleLightningStrikeConfig[]>([])

    const queueDestroyBurstForObstacle = (obstacleId: string) => {
        if (emittedBurstIdsRef.current.has(obstacleId)) {
            return
        }

        const obstacleInstance = obstaclesController.obstacleInstancesById.get(obstacleId)
        const localBounds = obstaclesController.obstacleLocalBoundsById.get(obstacleId)
        if (!obstacleInstance || !localBounds) {
            return
        }

        const center = localBounds.getCenter(new THREE.Vector3())
        center.applyQuaternion(obstacleInstance.worldRotation)
        center.add(obstacleInstance.worldPosition)

        const halfExtents = localBounds.getSize(new THREE.Vector3()).multiplyScalar(0.5)

        destroyBurstQueueRef.current.push({
            center,
            halfExtents,
            rotation: obstacleInstance.worldRotation.clone(),
        })
        emittedBurstIdsRef.current.add(obstacleId)
    }

    useEffect(() => {
        obstaclesController.update(
            trackController.getTrackPlaybackState().trackPosition,
            runState.hiddenObstacleIds,
        )
    }, [obstaclesController, trackController])

    useEffect(() => {
        const handleBatteryLightning = (event: Event) => {
            const customEvent = event as CustomEvent<BatteryObstacleLightningEventDetail>
            const obstacleIds = customEvent.detail?.obstacleIds ?? []

            obstacleIds.forEach((obstacleId) => {
                const obstacleInstance = obstaclesController.obstacleInstancesById.get(obstacleId)
                const localBounds = obstaclesController.obstacleLocalBoundsById.get(obstacleId)
                if (!obstacleInstance || !localBounds) {
                    return
                }

                const center = localBounds.getCenter(new THREE.Vector3())
                center.applyQuaternion(obstacleInstance.worldRotation)
                center.add(obstacleInstance.worldPosition)

                const halfExtents = localBounds.getSize(new THREE.Vector3()).multiplyScalar(0.5)

                batteryStrikeQueueRef.current.push({
                    center,
                    halfExtents,
                })
            })
        }

        window.addEventListener(BATTERY_OBSTACLE_LIGHTNING_EVENT, handleBatteryLightning as EventListener)
        return () => {
            window.removeEventListener(BATTERY_OBSTACLE_LIGHTNING_EVENT, handleBatteryLightning as EventListener)
        }
    }, [obstaclesController])

    useFrame(() => {
        if (runState.phase === 'idle' && runState.hiddenObstacleIds.size === 0) {
            processedHiddenIdsRef.current = new Set()
            emittedBurstIdsRef.current = new Set()
        }

        const nextProcessedHiddenIds = new Set<string>()
        runState.hiddenObstacleIds.forEach((obstacleId) => {
            nextProcessedHiddenIds.add(obstacleId)
            if (processedHiddenIdsRef.current.has(obstacleId)) {
                return
            }
            queueDestroyBurstForObstacle(obstacleId)
        })
        processedHiddenIdsRef.current = nextProcessedHiddenIds

        const obstacleVisibilityHiddenIds = runState.phase === 'gameOver'
            ? new Set(obstaclesController.obstacleInstancesById.keys())
            : runState.hiddenObstacleIds
        obstaclesController.update(
            trackController.getTrackPlaybackState().trackPosition,
            obstacleVisibilityHiddenIds,
        )

        if (runState.phase !== 'running') return
        if (runState.snapshot.hitFreezeTimer > 0) return
        if (runState.snapshot.hitReactionTimer > 0) return

        const snapshot = runState.snapshot
        const trackPosition = trackController.getTrackPlaybackState().trackPosition

        const collisionMatch = checkObstacleCollision(
            trackPosition,
            snapshot.laneVisualX,
            snapshot.jumpOffset,
            collisionData,
        )

        if (!collisionMatch) return

        const { index: hitIndex, stopTrackPosition, hitAnimationCategory } = collisionMatch
        const obstacleId = `obstacle-path-${hitIndex}`

        if (runState.hiddenObstacleIds.has(obstacleId)) return
        if (store.debugInvincible) return

        if (runState.snapshot.planeTimer > 0) {
            runState.hideObstacle(obstacleId)
            return
        }

        if (runState.snapshot.shieldActive) {
            vibrate([40, 40])
            runState.consumeShield(obstacleId)
            return
        }

        trackController.setTrackPosition(
            Math.min(trackPosition, stopTrackPosition),
        )

        runState.applyObstacleHit(obstacleId, hitAnimationCategory)
    })

    return (
        <>
            <primitive object={obstaclesController.group} />
            <ObstacleDestroyEffect
                consumeBurstQueue={() => {
                    const queuedBursts = destroyBurstQueueRef.current
                    destroyBurstQueueRef.current = []
                    return queuedBursts
                }}
            />
            <BatteryObstacleLightningEffect
                consumeStrikeQueue={() => {
                    const queuedStrikes = batteryStrikeQueueRef.current
                    batteryStrikeQueueRef.current = []
                    return queuedStrikes
                }}
            />
            <ObstacleHitboxDebug
                collisionData={collisionData}
                trackController={trackController}
            />
        </>
    )
}

function createObstacles(
    assetsByRegion: ObstacleAssetsByRegion,
    placements: readonly ResolvedObstaclePlacement[],
    trackController: TrackController,
): ObstaclesController {
    const flipRotation = new THREE.Quaternion().setFromEuler(
        new THREE.Euler(0, Math.PI, 0),
    )
    const obstacleInstances: ObstacleInstance[] = placements.map(
        (placement) => {
            const frame = trackController.frameAtPosition(placement.position)
            const rotation = frame.cameraQuaternion.clone()
            const offset = new THREE.Vector3(lanePositions[placement.track], 0, 0).applyQuaternion(rotation)
            if (placement.flip === 1) {
                rotation.multiply(flipRotation)
            }
            const asset = assetsByRegion[placement.region][placement.type] ?? null
            return {
                sourceIndex: placement.sourceIndex,
                position: placement.position,
                type: placement.type,
                region: placement.region,
                asset,
                worldPosition: frame.position.clone().add(offset),
                worldRotation: rotation,
            }
        },
    )

    const obstacleGroup = new THREE.Group()
    const scale = new THREE.Vector3()
    const matrix = new THREE.Matrix4()
    const instancedMeshesByAsset = new Map<THREE.Object3D, THREE.InstancedMesh[]>()
    const instanceIndexesByAsset = new Map<THREE.Object3D, number[]>()
    const obstacleInstancesById = new Map<string, ObstacleInstance>()
    const obstacleLocalBoundsById = new Map<string, THREE.Box3>()
    const obstacleAssets = Array.from(
        new Set(
            placements
                .map((placement) => assetsByRegion[placement.region][placement.type])
                .filter((asset): asset is THREE.Object3D => Boolean(asset)),
        ),
    )

    obstacleInstances.forEach((instance) => {
        const obstacleId = `obstacle-path-${instance.sourceIndex}`
        obstacleInstancesById.set(obstacleId, instance)

        if (!instance.asset) {
            return
        }

        const localBounds = new THREE.Box3().setFromObject(instance.asset)
        obstacleLocalBoundsById.set(obstacleId, localBounds)
    })

    for (const asset of obstacleAssets) {
        const instancesForType = obstacleInstances.filter((instance, index) => {
            return assetsByRegion[instance.region][instance.type] === asset
        })
        if (instancesForType.length === 0) {
            continue
        }
        const obstacleMatrices = instancesForType.map((instance) =>
            new THREE.Matrix4().compose(
                instance.worldPosition,
                instance.worldRotation,
                new THREE.Vector3(0, 0, 0),
            ),
        )
        const obstacleTypeGroup = createInstancedAssetGroup(
            asset,
            obstacleMatrices,
        )
        const instancedMeshes = obstacleTypeGroup.children.filter(
            (child): child is THREE.InstancedMesh =>
                child instanceof THREE.InstancedMesh,
        )

        obstacleGroup.add(obstacleTypeGroup)
        instancedMeshesByAsset.set(asset, instancedMeshes)
        instanceIndexesByAsset.set(
            asset,
            obstacleInstances.flatMap((instance, index) =>
                assetsByRegion[instance.region][instance.type] === asset
                    ? [index]
                    : [],
            ),
        )
    }

    return {
        group: obstacleGroup,
        obstacleInstancesById,
        obstacleLocalBoundsById,
        update(trackPosition: number, hiddenIds?: Set<string>) {
            for (const asset of obstacleAssets) {
                const instancedMeshes = instancedMeshesByAsset.get(asset) ?? []
                const instanceIndexes = instanceIndexesByAsset.get(asset) ?? []

                instanceIndexes.forEach((instanceIndex, typeIndex) => {
                    const instance = obstacleInstances[instanceIndex]

                    const id = `obstacle-path-${instance.sourceIndex}`
                    if (hiddenIds?.has(id)) {
                        matrix.compose(
                            instance.worldPosition,
                            instance.worldRotation,
                            scale.setScalar(0),
                        )
                        instancedMeshes.forEach((instancedMesh) => {
                            instancedMesh.setMatrixAt(typeIndex, matrix)
                        })
                        return
                    }

                    const delta = trackController.positionDeltaOnTrack(
                        instance.position,
                        trackPosition,
                    )
                    const blend = THREE.MathUtils.inverseLerp(
                        obstacleScaleStartDistance,
                        obstacleScaleEndDistance,
                        delta,
                    )
                    const instanceScale = THREE.MathUtils.smoothstep(blend, 0, 1)
                    matrix.compose(
                        instance.worldPosition,
                        instance.worldRotation,
                        scale.setScalar(instanceScale),
                    )
                    instancedMeshes.forEach((instancedMesh) => {
                        instancedMesh.setMatrixAt(typeIndex, matrix)
                    })
                })

                instancedMeshes.forEach((instancedMesh) => {
                    instancedMesh.instanceMatrix.needsUpdate = true
                })
            }
        },
    }
}


